import { create } from "zustand";
import {
  persist,
  PersistOptions,
  subscribeWithSelector,
} from "zustand/middleware";

import { createAuthSlice, type AuthSlice } from "./slices/createAuthSlice";
import {
  type BasketSlice,
  createBasketSlice,
} from "./slices/createBasketSlice";

import createBoxOfficeConfigSlice, {
  type BoxOfficeConfigSlice,
} from "./slices/createBoxOfficeConfigSlice";
import createCheckoutSlice, {
  type CheckoutSlice,
} from "./slices/createCheckoutSlice";

/**
 * Extend this type to add more slices to the store
 * as they are created.
 *
 * @example type Store = AuthSlice & AnotherOne & AnotherOne
 */
export type Store = AuthSlice &
  BasketSlice &
  CheckoutSlice &
  BoxOfficeConfigSlice;

const persistedStore = (options?: Omit<PersistOptions<Store, Store>, "name">) =>
  persist<Store>(
    (...a) => ({
      ...createAuthSlice(...a),
      ...createBasketSlice(...a),
      ...createCheckoutSlice(...a),
      ...createBoxOfficeConfigSlice(...a),
    }),
    {
      name: "showpass-storage",
      partialize: (state) =>
        Object.fromEntries(
          Object.entries(state).filter(
            ([key]) => !["isBasketUpdating", "basketError"].includes(key)
          )
        ) as Store,
      ...options,
    }
  );

/**
 * In a typical state management setup, whenever a state update occurs, all subscribers are notified,
 * often causing components to re-render even if the specific state change is irrelevant to them.
 * subscribeWithSelector allows selective subscriptions, meaning the component or function only gets notified when the part of the state it cares about has changed.
 */
const selectableStoreMiddleware = subscribeWithSelector;

export const useStore = create(
  selectableStoreMiddleware(
    persistedStore({
      // Temporarily remove currentVenue from the store when rehydrating.
      // We removed this as part of https://github.com/showpass/showpass-frontend/pull/3312 but it will still be in
      // people's local storage, until they use this version of the web/desktop app.
      // CLEANUP TASK -- https://showpass.atlassian.net/browse/SPW-14164
      onRehydrateStorage: () => {
        return (state) => {
          if (state) {
            // @ts-expect-error - currentVenue is not part of the store
            if (state.currentVenue) {
              // @ts-expect-error - currentVenue is not part of the store
              // eslint-disable-next-line no-param-reassign
              delete state.currentVenue;
            }
          }
        };
      },
    })
  )
);
